import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../common/BackButton';
import { tagsAPI } from '../../services/garageApi';
import {
  Save,
  Tag,
  AlertCircle
} from 'lucide-react';

const TagCreate = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        section: ''
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Tag name is required';
        } else if (formData.name.length < 2) {
            newErrors.name = 'Tag name must be at least 2 characters';
        } else if (formData.name.length > 50) {
            newErrors.name = 'Tag name must not exceed 50 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setSaving(true);
        try {
            await tagsAPI.create({
                name: formData.name.trim(),
                section: formData.section.trim()
            });

            navigate('/tags', {
                state: { message: 'Tag created successfully!' }
            });
        } catch (error) {
            console.error('Error creating tag:', error);
            
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else if (error.response?.data?.message) {
                setErrors({ submit: error.response.data.message });
            } else {
                setErrors({ submit: 'Failed to create tag. Please try again.' });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <BackButton to="/tags" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Add New Tag</h1>
                        <p className="text-sm text-gray-600 mt-1">Create a new product tag for categorization</p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                {errors.submit && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-5 h-5" />
                        <span>{errors.submit}</span>
                    </div>
                )}

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tag Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="e.g., Premium, Budget, Seasonal"
                                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    errors.name ? 'border-red-300' : 'border-gray-200'
                                }`}
                            />
                        </div>
                        {errors.name && (
                            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                        )}
                        <p className="mt-2 text-sm text-gray-500">
                            Enter a descriptive name for this tag
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Section
                        </label>
                        <input
                            type="text"
                            name="section"
                            value={formData.section}
                            onChange={handleChange}
                            placeholder="e.g., Tires, Batteries, General"
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="mt-2 text-sm text-gray-500">
                            Optional: Specify which section this tag belongs to
                        </p>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
                    <button
                        type="button"
                        onClick={() => navigate('/tags')}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                Create Tag
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TagCreate;